import { ApiError } from "../../lib/api";

// g-01 応募フォームの送信前検証・リクエスト整形・API エラーの文言化（tst-eh50）。
//
// 送信は <form> submit ではなく Button の onClick 経由なので、<input type="email"> の
// ネイティブ検証は自動では発火しない。ここで明示的に判定し、不正なら API を呼ばずに
// 項目に即した文言を返す。API 側（zod .email()）の 400 validation_failed も同じ文言へ落とす。

export type TimeAvail = "AM" | "PM" | "FullDay";

export type ApplicationForm = {
  fullName: string;
  japaneseName: string;
  nickname: string;
  area: string;
  phone: string;
  email: string;
  selfIntro: string;
  englishBackground: string;
  weekdays: string[];
  time: TimeAvail | null;
};

/** POST /guide-applications の body（apps/api ApplicationSchema と同形）。 */
export type ApplicationRequest = {
  fullName: string;
  japaneseName?: string;
  nickname?: string;
  area?: string;
  phone?: string;
  email: string;
  selfIntro?: string;
  englishBackground?: string;
  availability: { weekdays: string[]; time?: TimeAvail };
};

export const REQUIRED_MESSAGE = "Full name and email are required.";
export const INVALID_EMAIL_MESSAGE = "Please enter a valid email address.";
export const RATE_LIMITED_MESSAGE =
  "Too many submissions from your network right now. Please wait a little and try again.";
export const GENERIC_FAILURE_MESSAGE =
  "We couldn't submit your application. Please check your details and try again.";
export const NETWORK_ERROR_MESSAGE = "Network error. Please try again.";

/** ネイティブ検証に使う <input type="email"> の最小インターフェース（未マウント時は null）。 */
export type EmailInputLike = Pick<HTMLInputElement, "validity"> | null | undefined;

// ネイティブ検証が使えないとき（要素未マウント・validity 非対応）のフォールバック。
// zod の .email() と厳密に一致させる必要はない（すり抜けた分はサーバの 400 を messageForApiError が拾う）。
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** email の形式判定。ブラウザのネイティブ検証（validity.typeMismatch）を優先し、無ければ正規表現。 */
export function isValidEmail(value: string, input?: EmailInputLike): boolean {
  const v = value.trim();
  if (!v) return false;
  if (input?.validity) return !input.validity.typeMismatch;
  return EMAIL_RE.test(v);
}

/**
 * 送信前検証。エラー文言を返し、問題なければ null。
 * 空欄チェック（Full name / Email）の文言と順序は従来どおり、その後に email 形式を見る。
 */
export function validateApplicationForm(
  form: Pick<ApplicationForm, "fullName" | "email">,
  emailInput?: EmailInputLike,
): string | null {
  if (!form.fullName.trim() || !form.email.trim()) return REQUIRED_MESSAGE;
  if (!isValidEmail(form.email, emailInput)) return INVALID_EMAIL_MESSAGE;
  return null;
}

/** フォーム状態を API リクエストへ整形する（任意項目はトリム後の空を未送信にする）。 */
export function toApplicationRequest(form: ApplicationForm): ApplicationRequest {
  return {
    fullName: form.fullName.trim(),
    japaneseName: form.japaneseName.trim() || undefined,
    nickname: form.nickname.trim() || undefined,
    area: form.area || undefined,
    phone: form.phone.trim() || undefined,
    email: form.email.trim(),
    selfIntro: form.selfIntro.trim() || undefined,
    englishBackground: form.englishBackground.trim() || undefined,
    availability: {
      weekdays: form.weekdays,
      time: form.time ?? undefined,
    },
  };
}

// 400 validation_failed の details.fieldErrors（zod flatten）のキー → 画面ラベル。
const FIELD_LABELS: Record<string, string> = {
  fullName: "Full name",
  japaneseName: "Japanese name",
  nickname: "English nickname",
  area: "Area of residence",
  phone: "Phone",
  email: "Email",
  selfIntro: "Self introduction",
  englishBackground: "English background",
  availability: "Availability",
};

/** 400 validation_failed の body からエラーのあるフィールド名を取り出す。該当なしは []。 */
function invalidFieldsOf(body: unknown): string[] {
  if (typeof body !== "object" || body === null) return [];
  const b = body as { error?: unknown; details?: { fieldErrors?: unknown } };
  if (b.error !== "validation_failed") return [];
  const fieldErrors = b.details?.fieldErrors;
  if (typeof fieldErrors !== "object" || fieldErrors === null) return [];
  return Object.entries(fieldErrors as Record<string, unknown>)
    .filter(([, v]) => Array.isArray(v) && v.length > 0)
    .map(([k]) => k);
}

/** API 呼び出しの失敗を画面の文言へ落とす。 */
export function messageForApiError(e: unknown): string {
  if (!(e instanceof ApiError)) return NETWORK_ERROR_MESSAGE;
  // 429 = レート制限（tst-2qn / bot 連打抑止）。原因（窓・閾値）は伏せた一般化メッセージ。
  if (e.status === 429) return RATE_LIMITED_MESSAGE;
  if (e.status === 400) {
    const fields = invalidFieldsOf(e.body);
    if (fields.length === 1 && fields[0] === "email") return INVALID_EMAIL_MESSAGE;
    if (fields.length > 0) {
      const labels = fields.map((f) => FIELD_LABELS[f] ?? f).join(", ");
      return `Please check the following and try again: ${labels}.`;
    }
  }
  return GENERIC_FAILURE_MESSAGE;
}

export type SubmitResult = { ok: true } | { ok: false; error: string };

/**
 * 応募の送信。検証を通った場合だけ deps.post を呼ぶ（不正入力では API に到達しない）。
 * 画面側は結果の error をそのまま既存のエラー表示領域に出す。
 */
export async function submitApplication(
  form: ApplicationForm,
  deps: {
    emailInput?: EmailInputLike;
    post: (body: ApplicationRequest) => Promise<unknown>;
  },
): Promise<SubmitResult> {
  const invalid = validateApplicationForm(form, deps.emailInput);
  if (invalid) return { ok: false, error: invalid };
  try {
    await deps.post(toApplicationRequest(form));
    return { ok: true };
  } catch (e) {
    return { ok: false, error: messageForApiError(e) };
  }
}
